'use client';

import React, { useState, useEffect } from 'react';
import { useRole } from '@/contexts/RoleContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { createClient } from '@/lib/supabase/client';
import { Users, MapPin, Phone, ShoppingCart, LogOut, RefreshCw, Star, TrendingUp, DollarSign } from 'lucide-react';

interface Retailer {
  id: number;
  name: string;
  zone: string;
  phone: string;
  credit_balance: number;
  last_order_date: string | null;
}

export default function MobileSalesRepView() {
  const { activeStaff, logoutRole } = useRole();
  const { language, dir } = useLanguage();
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'clients' | 'orders' | 'stats'>('clients');

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('retailers')
      .select('id, name, zone, phone, credit_balance, last_order_date')
      .order('name')
      .then(({ data }) => {
        setRetailers(data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const totalCredit = retailers.reduce((sum, r) => sum + (r.credit_balance || 0), 0);

  return (
    <div className="min-h-screen bg-background flex flex-col max-w-md mx-auto" dir={dir}>
      {/* Header */}
      <div className="bg-sky-600 text-white px-4 pt-safe-top pb-4 sticky top-0 z-20">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs opacity-80">{language === 'ar' ? 'مرحباً،' : 'Bonjour,'}</p>
            <h1 className="text-base font-800">{activeStaff?.staffName}</h1>
          </div>
          <button
            onClick={logoutRole}
            className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <LogOut size={15} />
          </button>
        </div>
        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/15 rounded-xl p-2.5 text-center">
            <p className="text-lg font-800">{retailers.length}</p>
            <p className="text-2xs opacity-80">{language === 'ar' ? 'زبون' : 'Clients'}</p>
          </div>
          <div className="bg-white/15 rounded-xl p-2.5 text-center">
            <p className="text-lg font-800">0</p>
            <p className="text-2xs opacity-80">{language === 'ar' ? 'طلب اليوم' : 'Cmd. Auj.'}</p>
          </div>
          <div className="bg-white/15 rounded-xl p-2.5 text-center">
            <p className="text-sm font-800">{(totalCredit / 1000).toFixed(1)}k</p>
            <p className="text-2xs opacity-80">{language === 'ar' ? 'كريدي' : 'Crédit'}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border-b border-border sticky top-[120px] z-10">
        {[
          { key: 'clients', labelAr: 'الزبائن', labelFr: 'Clients', icon: Users },
          { key: 'orders', labelAr: 'الطلبات', labelFr: 'Commandes', icon: ShoppingCart },
          { key: 'stats', labelAr: 'الإحصاء', labelFr: 'Stats', icon: TrendingUp },
        ].map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex-1 flex flex-col items-center gap-1 py-3 text-2xs font-600 transition-colors border-b-2 ${
                activeTab === tab.key
                  ? 'border-sky-500 text-sky-600' :'border-transparent text-muted-foreground'
              }`}
            >
              <TabIcon size={16} />
              {language === 'ar' ? tab.labelAr : tab.labelFr}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-safe-bottom">
        {activeTab === 'clients' && (
          <div className="p-4 space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw size={20} className="animate-spin text-muted-foreground" />
              </div>
            ) : retailers.length === 0 ? (
              <div className="text-center py-12">
                <Users size={40} className="text-muted-foreground mx-auto mb-3 opacity-40" />
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'لا يوجد زبائن' : 'Aucun client'}
                </p>
              </div>
            ) : (
              retailers.map((retailer) => (
                <div key={retailer.id} className="bg-white rounded-2xl border border-border p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-700 text-foreground truncate">{retailer.name}</h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        <MapPin size={11} className="text-muted-foreground flex-shrink-0" />
                        <span className="text-2xs text-muted-foreground truncate">{retailer.zone || '—'}</span>
                      </div>
                    </div>
                    {retailer.credit_balance > 0 && (
                      <span className="text-2xs font-700 text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full flex-shrink-0">
                        {retailer.credit_balance.toLocaleString()} {language === 'ar' ? 'د' : 'MAD'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href={`tel:${retailer.phone}`}
                      className="flex items-center gap-1.5 text-2xs text-sky-600 font-600 bg-sky-50 border border-sky-200 px-2.5 py-1.5 rounded-xl"
                    >
                      <Phone size={12} />
                      {retailer.phone || language === 'ar' ? 'اتصال' : 'Appeler'}
                    </a>
                    <a
                      href={`https://wa.me/${retailer.phone?.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-2xs text-green-600 font-600 bg-green-50 border border-green-200 px-2.5 py-1.5 rounded-xl"
                    >
                      <span className="text-xs">💬</span>
                      WhatsApp
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="p-4">
            <div className="text-center py-12">
              <ShoppingCart size={40} className="text-muted-foreground mx-auto mb-3 opacity-40" />
              <p className="text-sm font-600 text-foreground mb-1">
                {language === 'ar' ? 'طلبات اليوم' : 'Commandes du jour'}
              </p>
              <p className="text-xs text-muted-foreground">
                {language === 'ar' ? 'لا توجد طلبات بعد' : 'Aucune commande pour le moment'}
              </p>
            </div>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="p-4 space-y-3">
            <div className="bg-white rounded-2xl border border-border p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center">
                  <DollarSign size={16} className="text-sky-600" />
                </div>
                <div>
                  <p className="text-xs font-700 text-foreground">
                    {language === 'ar' ? 'إجمالي الكريدي' : 'Total Crédit'}
                  </p>
                  <p className="text-lg font-800 text-foreground">{totalCredit.toLocaleString()} MAD</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-border p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center">
                  <Star size={16} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-700 text-foreground">
                    {language === 'ar' ? 'عدد الزبائن' : 'Nombre de clients'}
                  </p>
                  <p className="text-lg font-800 text-foreground">{retailers.length}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
